import React from 'react';
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  MenuItem,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import RestartAltRoundedIcon from '@mui/icons-material/RestartAltRounded';
import PlaceOutlinedIcon from '@mui/icons-material/PlaceOutlined';
import MusicNoteOutlinedIcon from '@mui/icons-material/MusicNoteOutlined';
import { TutorSkill } from '../../models/Tutor';
import { tutorSkillLabel } from '../../utils/tutorSkillI18n';
import TutorSkillAutocomplete from './TutorSkillAutocomplete';
import {
  getTutorSessionTypeLabel,
  TUTOR_SESSION_TYPES,
  type TutorSessionType,
} from '../../utils/tutorSessionType';
import {
  type TutorOfflineSessionType,
  type TutorSearchMode,
} from '../../utils/tutorSearchMode';

const CONTROL_H = 44;
const COMPACT_FS = { xs: '0.78rem', sm: '0.82rem' } as const;
const COMPACT_LABEL_FS = { xs: '0.68rem', sm: '0.72rem' } as const;

const fieldSx = {
  '& .MuiInputBase-root': {
    minHeight: CONTROL_H,
    maxHeight: CONTROL_H,
  },
  '& .MuiOutlinedInput-root': {
    borderRadius: 1.25,
    backgroundColor: 'rgba(255, 255, 255, 0.06)',
    '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.14)' },
    '&:hover fieldset': { borderColor: 'rgba(78, 103, 235, 0.45)' },
    '&.Mui-focused fieldset': { borderColor: 'primary.main', borderWidth: 1.5 },
  },
  '& .MuiInputBase-input': {
    py: '10px',
    fontSize: COMPACT_FS,
    fontWeight: 500,
    color: 'rgba(255,255,255,0.9)',
  },
  '& .MuiInputLabel-root': {
    color: 'rgba(255,255,255,0.58)',
    fontSize: COMPACT_LABEL_FS,
    fontWeight: 600,
    '&.Mui-focused': { color: 'primary.main' },
  },
} as const;

const skillFieldSx = {
  ...fieldSx,
  '& .MuiInputBase-root': {
    minHeight: CONTROL_H,
    py: 0.5,
    alignItems: 'center',
  },
  '& .MuiChip-root': {
    height: 24,
    fontSize: '0.72rem',
  },
} as const;

/** Maps search mode + offline place into the flat three-way session type used by mobile. */
export function resolveSearchSessionType(
  mode: TutorSearchMode,
  offlineSessionType: TutorOfflineSessionType
): TutorSessionType {
  return mode === 'ONLINE' ? 'ONLINE' : offlineSessionType;
}

export interface TutorSearchToolbarProps {
  selectedMode: TutorSearchMode;
  onModeChange: (event: React.MouseEvent<HTMLElement>, nextMode: TutorSearchMode | null) => void;
  selectedOfflineSessionType: TutorOfflineSessionType;
  onOfflineSessionTypeChange: (sessionType: TutorOfflineSessionType) => void;
  selectedCity: string;
  onCityChange: (city: string) => void;
  /** Fired when the user picks a city from the list (applies search immediately). */
  onCitySelect?: (city: string) => void;
  cityError: string | null;
  citiesError: string | null;
  cityOptions: string[];
  citiesLoading: boolean;
  selectedSkills: TutorSkill[];
  onSkillsChange: (skills: TutorSkill[]) => void;
  onSearch: () => void;
  onReset: () => void;
  showTitle?: boolean;
  activeMode: TutorSearchMode;
  activeCity?: string;
  activeOfflineSessionType?: TutorOfflineSessionType;
  activeSkills?: TutorSkill[];
}

const TutorSearchToolbar: React.FC<TutorSearchToolbarProps> = ({
  selectedMode,
  onModeChange,
  selectedOfflineSessionType,
  onOfflineSessionTypeChange,
  selectedCity,
  onCityChange,
  onCitySelect,
  cityError,
  citiesError,
  cityOptions,
  citiesLoading,
  selectedSkills,
  onSkillsChange,
  onSearch,
  onReset,
  showTitle = false,
  activeMode,
  activeCity,
  activeOfflineSessionType = 'AT_TEACHER',
  activeSkills = [],
}) => {
  const { t } = useTranslation();

  const selectedSessionType = resolveSearchSessionType(selectedMode, selectedOfflineSessionType);
  const isOffline = selectedMode === 'OFFLINE';

  const hasActiveFilters =
    (activeMode === 'OFFLINE' && Boolean(activeCity?.trim())) ||
    (activeMode === 'OFFLINE' && activeOfflineSessionType === 'AT_STUDENT') ||
    activeSkills.length > 0;

  const handleSessionTypeChange = (next: TutorSessionType) => {
    if (!next || next === selectedSessionType) return;

    if (next === 'ONLINE') {
      onModeChange({} as React.MouseEvent<HTMLElement>, 'ONLINE');
      return;
    }

    // Offline place change already navigates to `/user/tutors/offline` with sessionType.
    onOfflineSessionTypeChange(next);
  };

  const handleEnterSearch = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      onSearch();
    }
  };

  return (
    <Stack spacing={1.5} sx={{ mb: 2.5 }}>
      {showTitle ? (
        <Box>
          <Typography
            variant="h5"
            sx={{
              fontWeight: 700,
              letterSpacing: '-0.02em',
              lineHeight: 1.2,
            }}
          >
            {t('navigation.tutorSearch', 'Tutor Search')}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.35 }}>
            {t('tutorSearch.subtitle', 'Find a tutor by instrument, language, or session type.')}
          </Typography>
        </Box>
      ) : null}

      <Stack
        direction={{ xs: 'column', md: 'row' }}
        spacing={{ xs: 1.25, md: 1 }}
        alignItems={{ xs: 'stretch', md: 'center' }}
      >
          <TextField
            select
            size="small"
            fullWidth
            label={t('tutorSearch.sessionType', 'Session type')}
            value={selectedSessionType}
            onChange={(event) => handleSessionTypeChange(event.target.value as TutorSessionType)}
            sx={{ ...fieldSx, flex: { md: '0 1 200px' }, minWidth: { md: 170 }, maxWidth: { md: 240 } }}
            inputProps={{ 'aria-label': t('tutorSearch.sessionType', 'Session type') }}
          >
            {TUTOR_SESSION_TYPES.map((type) => (
              <MenuItem key={type} value={type}>
                {getTutorSessionTypeLabel(type, t)}
              </MenuItem>
            ))}
          </TextField>

          {isOffline ? (
            citiesError ? (
              <TextField
                size="small"
                fullWidth
                label={t('tutorSearch.city', 'City')}
                value={selectedCity}
                onChange={(e) => onCityChange(e.target.value)}
                error={Boolean(cityError)}
                helperText={cityError || citiesError || undefined}
                onKeyDown={handleEnterSearch}
                sx={{ ...fieldSx, flex: { md: '1 1 180px' }, minWidth: { md: 160 } }}
              />
            ) : (
              <Autocomplete
                size="small"
                fullWidth
                options={cityOptions}
                value={selectedCity || null}
                onChange={(_, value) => {
                  const city = value ?? '';
                  onCityChange(city);
                  onCitySelect?.(city);
                }}
                loading={citiesLoading}
                isOptionEqualToValue={(option, value) => option === value}
                sx={{ flex: { md: '1 1 180px' }, minWidth: { md: 160 } }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={t('tutorSearch.city', 'City')}
                    error={Boolean(cityError)}
                    helperText={cityError || undefined}
                    onKeyDown={handleEnterSearch}
                    sx={fieldSx}
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {citiesLoading ? <CircularProgress color="inherit" size={18} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />
            )
          ) : null}

          <TutorSkillAutocomplete
            multiple
            size="small"
            value={selectedSkills}
            onChange={onSkillsChange}
            label={t('tutorSearch.skill', 'Skill')}
            placeholder={t('tutorSearch.skillsPlaceholder', 'Select skills')}
            onKeyDown={handleEnterSearch}
            textFieldSx={skillFieldSx}
            limitTags={2}
            sx={{ flex: { md: '1 1 220px' }, minWidth: { md: 180 } }}
          />

          <Stack
            direction="row"
            spacing={0.75}
            alignItems="center"
            sx={{ flexShrink: 0, width: { xs: '100%', md: 'auto' } }}
          >
            <Button
              variant="contained"
              onClick={onSearch}
              startIcon={<SearchRoundedIcon sx={{ fontSize: 20 }} />}
              sx={{
                flex: { xs: 1, md: '0 0 auto' },
                minHeight: CONTROL_H,
                px: { xs: 2, sm: 2.5 },
                borderRadius: 1.25,
                fontWeight: 700,
                fontSize: COMPACT_FS,
                letterSpacing: '0.04em',
                textTransform: 'none',
                boxShadow: '0 8px 22px rgba(78, 103, 235, 0.28)',
                '&:hover': {
                  boxShadow: '0 10px 26px rgba(78, 103, 235, 0.36)',
                },
              }}
            >
              {t('common.search', 'Search')}
            </Button>
            <Tooltip title={t('common.resetFilters', 'Reset filters')}>
              <IconButton
                aria-label={t('common.resetFilters', 'Reset filters')}
                onClick={onReset}
                sx={{
                  width: CONTROL_H,
                  height: CONTROL_H,
                  borderRadius: 1.25,
                  border: '1px solid rgba(255, 255, 255, 0.12)',
                  color: 'rgba(255,255,255,0.72)',
                  backgroundColor: 'rgba(255,255,255,0.03)',
                  '&:hover': {
                    backgroundColor: 'rgba(255,255,255,0.07)',
                    borderColor: 'rgba(255,255,255,0.2)',
                  },
                }}
              >
                <RestartAltRoundedIcon sx={{ fontSize: 20 }} />
              </IconButton>
            </Tooltip>
          </Stack>
      </Stack>

      {hasActiveFilters ? (
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
            {activeMode === 'OFFLINE' && activeOfflineSessionType === 'AT_STUDENT' ? (
              <Chip
                size="small"
                variant="outlined"
                label={getTutorSessionTypeLabel('AT_STUDENT', t)}
                sx={{
                  height: 26,
                  borderColor: 'rgba(255,255,255,0.14)',
                  color: 'rgba(255,255,255,0.78)',
                }}
              />
            ) : null}
            {activeMode === 'OFFLINE' && activeCity ? (
              <Chip
                size="small"
                variant="outlined"
                icon={<PlaceOutlinedIcon />}
                label={activeCity}
                sx={{
                  height: 26,
                  borderColor: 'rgba(255,255,255,0.14)',
                  color: 'rgba(255,255,255,0.78)',
                  '& .MuiChip-icon': { fontSize: 15 },
                }}
              />
            ) : null}
            {activeSkills.map((skill) => (
              <Chip
                key={skill}
                size="small"
                variant="outlined"
                icon={<MusicNoteOutlinedIcon />}
                label={tutorSkillLabel(t, skill)}
                sx={{
                  height: 26,
                  borderColor: 'rgba(78, 103, 235, 0.35)',
                  color: 'rgba(232, 236, 255, 0.92)',
                  backgroundColor: 'rgba(78, 103, 235, 0.1)',
                  '& .MuiChip-icon': { fontSize: 15, color: 'primary.light' },
                }}
              />
            ))}
        </Stack>
      ) : null}
    </Stack>
  );
};

export default TutorSearchToolbar;
